import React from "react";
import { PencilSimple, Trash, User, Calendar, Package } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import { formatUnit } from "@/lib/lorryApi";

export default function LorryTable({ lorries, loading, onEdit, onDelete }) {
  if (loading) {
    return (
      <div data-testid="table-loading" className="p-12 text-center text-zinc-500 border border-zinc-800">
        Loading entries…
      </div>
    );
  }

  if (!lorries.length) {
    return (
      <div
        data-testid="table-empty"
        className="p-8 sm:p-12 text-center border border-dashed border-zinc-800 bg-zinc-900/30"
      >
        <div className="overline text-zinc-500 mb-2">Empty Yard</div>
        <p className="text-zinc-400 text-sm">
          No lorry entries yet. Tap <span className="text-yellow-500 font-semibold">+ New Entry</span> to register a load.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop / tablet: dense table */}
      <div className="hidden md:block border border-zinc-800 overflow-x-auto bg-zinc-950">
        <table data-testid="lorry-table" className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/60">
              <Th>Registration</Th>
              <Th>Product</Th>
              <Th className="text-right">Load</Th>
              <Th>Driver</Th>
              <Th>Date</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {lorries.map((l, idx) => (
              <tr
                key={l.id}
                data-testid={`row-${l.id}`}
                className="row-in border-b border-zinc-900 hover:bg-zinc-900/50 transition-colors"
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <Td>
                  <span className="font-mono font-semibold text-zinc-100 tracking-wider">
                    {l.registration}
                  </span>
                </Td>
                <Td className="text-zinc-300">{l.product}</Td>
                <Td className="text-right whitespace-nowrap">
                  <span className="font-display font-bold text-zinc-100">
                    {l.load_amount}
                  </span>
                  <span className="overline text-zinc-500 ml-1">{formatUnit(l.load_unit)}</span>
                </Td>
                <Td className="text-zinc-400">{l.driver_name}</Td>
                <Td className="text-zinc-400 font-mono">{l.date}</Td>
                <Td><StatusBadge status={l.status} /></Td>
                <Td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      data-testid={`edit-${l.id}`}
                      onClick={() => onEdit(l)}
                      className="p-2 text-zinc-500 hover:text-yellow-500 hover:bg-zinc-800 transition-colors rounded-sm"
                      title="Edit"
                    >
                      <PencilSimple size={16} weight="bold" />
                    </button>
                    <button
                      data-testid={`delete-${l.id}`}
                      onClick={() => onDelete(l)}
                      className="p-2 text-zinc-500 hover:text-red-500 hover:bg-zinc-800 transition-colors rounded-sm"
                      title="Delete"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        {lorries.map((l, idx) => (
          <div
            key={l.id}
            data-testid={`row-${l.id}`}
            style={{ animationDelay: `${idx * 30}ms` }}
            className="row-in border border-zinc-800 bg-zinc-900/40 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="overline text-zinc-500 mb-1">Registration</div>
                <div className="font-mono font-bold text-zinc-100 text-base tracking-wider break-all">
                  {l.registration}
                </div>
              </div>
              <StatusBadge status={l.status} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <MobileField icon={<Package size={13} weight="bold" />} label="Product">
                <span className="text-zinc-200 text-sm">{l.product}</span>
              </MobileField>
              <MobileField label="Load">
                <span className="font-display font-bold text-zinc-100 text-base">
                  {l.load_amount}
                </span>
                <span className="overline text-zinc-500 ml-1">{formatUnit(l.load_unit)}</span>
              </MobileField>
              <MobileField icon={<User size={13} weight="bold" />} label="Driver">
                <span className="text-zinc-300 text-sm">{l.driver_name}</span>
              </MobileField>
              <MobileField icon={<Calendar size={13} weight="bold" />} label="Date">
                <span className="text-zinc-300 font-mono text-sm">{l.date}</span>
              </MobileField>
            </div>

            <div className="mt-4 pt-3 border-t border-zinc-800 flex justify-end gap-1">
              <button
                data-testid={`edit-${l.id}`}
                onClick={() => onEdit(l)}
                className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-zinc-400 hover:text-yellow-500 hover:bg-zinc-800 transition-colors rounded-sm inline-flex items-center gap-1.5"
              >
                <PencilSimple size={14} weight="bold" /> Edit
              </button>
              <button
                data-testid={`delete-${l.id}`}
                onClick={() => onDelete(l)}
                className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-zinc-400 hover:text-red-500 hover:bg-zinc-800 transition-colors rounded-sm inline-flex items-center gap-1.5"
              >
                <Trash size={14} weight="bold" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function MobileField({ label, icon, children }) {
  return (
    <div>
      <div className="overline text-zinc-500 mb-1 inline-flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`overline text-zinc-500 text-left px-4 py-3 font-semibold ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-4 ${className}`}>{children}</td>;
}
